import dayjs from 'dayjs';
import { PropTypes } from 'prop-types';
import React, {
  useCallback,
  useState,
  useEffect,
  useImperativeHandle,
  useRef,
  useMemo,
} from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { DatePicker } from '@ge/components/datepicker';
import { Icon, Icons } from '@ge/components/icon';
import { Input } from '@ge/components/input';
import { Loader } from '@ge/components/loader';
import { Select } from '@ge/components/select';
import { Textarea } from '@ge/components/textarea';
import { Text } from '@ge/components/typography';
import { DateTimeFormats } from '@ge/models/constants';
import { getDayEndTime } from '@ge/shared/util';
import { typography } from '@ge/tokens';

import { useManualAdjustmentContext } from '../../context/manual-adjustment-context';
import { maLegendValues } from '../../models/manual-adjustment-legends';

const DateValue = styled.div`
  font-size: 11px;
  line-height: 20px;
  text-align: center;
  padding: 2px 14px;
  border-radius: 2px;
  max-width: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
  background: ${(props) => props.theme.button.primary.backgroundColor};
  border: ${(props) => props.theme.button.primary.border};
  color: ${(props) => props.theme.button.primary.textColor};
  text-shadow: ${(props) => props.theme.button.primary.textShadow};

  &.react-datepicker__tab-loop
    .react-datepicker-popper
    .react-datepicker
    .react-datepicker__children-container {
    width: 10.8rem !important;
  }
`;

const CalendarIcon = styled(Icon).attrs(({ theme }) => ({
  size: 12,
  color: theme.analyze.header.calendarIconColor,
  icon: Icons.CALENDAR,
}))`
  margin-left: 6px;
  vertical-align: initial;
`;

const ErrorText = styled(Text).attrs(() => ({
  type: typography.textTypes.body,
  level: 2,
}))`
  margin: 2px -3px -10px -2px;
  color: ${(props) => props.theme.dangerColor};
  display: block;
  .custom-body-2 {
    font-size: 11px;
    line-height: 10px;
  }
`;

import {
  StyledContainer,
  DialogLabel,
  DialogValue,
  IconButton,
  EventCategory,
  EventCategoryName,
  EventCategoryColor,
  StyledSelect,
  TimeIcon,
  AddNoteFormRow,
  EventNoteContainerHeading,
  StyledIconButtonIcon,
  DialogDisplay,
} from './event-dialog-style';

const AddEventDetailDialog = (
  {
    index,
    malCategoryOption,
    categoryOptions,
    eventOptions,
    calculateDuration,
    timezone,
    formatTime,
    assetEventsKind,
    eventDateDuration,
  },
  ref,
) => {
  const { t, ready } = useTranslation(['analyze.manual-adjustment'], {
    useSuspense: false,
  });

  const { selectedEvent, calendarDate } = useManualAdjustmentContext();
  const { event } = selectedEvent;
  const { errors, control } = useFormContext();

  const [selectEventCategory, setSelectEventCategory] = useState();
  const [selectEventName, setSelectEventName] = useState();
  const defaultStartTime = dayjs(event.startTime).tz(timezone);
  const defaultEndTime = dayjs(
    getDayEndTime(event.endTime, timezone, calendarDate, event?.type === 'IEC'),
  ).tz(timezone);
  const [startTime, setStartTime] = useState(dayjs(defaultStartTime).$d);
  const [endTime, setEndTime] = useState(dayjs(defaultEndTime).$d);
  const [showStartTime, setShowStartTime] = useState(dayjs(defaultStartTime).format('HH:mm:ss'));
  const [showEndTime, setShowEndTime] = useState(dayjs(defaultEndTime).format('HH:mm:ss'));
  const [timeDuration, setTimeDuration] = useState();
  const [noteEntered, setNoteEntered] = useState();
  const [eventDate, setEventDate] = useState({
    startDate:
      eventDateDuration?.startDate || dayjs(event.startTime).tz(timezone).startOf('day').valueOf(),
    endDate:
      eventDateDuration?.endDate || dayjs(event.endTime).tz(timezone).startOf('day').valueOf(),
  });
  const [dateError, setDateError] = useState(false);
  const datepickerRef = useRef();

  useEffect(() => {
    setTimeDuration(calculateDuration(eventDate.startDate, startTime, eventDate.endDate, endTime));
  }, [startTime, endTime, calculateDuration, eventDate.startDate, eventDate.endDate]);

  useImperativeHandle(ref, () => ({
    setTimeDuration,
  }));

  const handleEventNameChange = useCallback(
    (value) => {
      // eslint-disable-next-line no-unsafe-optional-chaining
      const { eventCategory } = malCategoryOption?.find((data) => data.id === value);
      setSelectEventCategory(eventCategory);
      setSelectEventName(value);
    },
    [malCategoryOption],
  );

  const onStartTimeChange = (value) => {
    setStartTime(value);
    setTimeDuration(calculateDuration(eventDate.startDate, value, eventDate.endDate, endTime));
  };

  const onEndTimeChange = (value) => {
    setEndTime(value);
    setTimeDuration(calculateDuration(eventDate.startDate, startTime, eventDate.endDate, value));
  };

  const onAddNoteChange = (value) => {
    setNoteEntered(value);
  };

  const getCustomDateString = () => {
    const start = dayjs(eventDate.startDate).format(DateTimeFormats.DEFAULT_DATE);
    const end = eventDate.endDate && dayjs(eventDate.endDate).format(DateTimeFormats.DEFAULT_DATE);

    return `${start} ${end && start !== end ? `- ${end}` : ''}`;
  };

  const eventDateError = useMemo(() => {
    return (
      <ErrorText>
        <span className="custom-body-2">
          {t(
            'event_details_dialog.invalid_startDate_error',
            'Start Date should be less than today.',
          )}
        </span>
      </ErrorText>
    );
  }, [t]);

  if (!ready) {
    return <Loader />;
  }

  return (
    <>
      <EventCategory>
        <EventCategoryColor size={'8px'} backgroundColor="#38434E" />
        <EventCategoryName>{t('event_details_dialog.new_event', 'New Event')}</EventCategoryName>
      </EventCategory>
      <EventCategory>
        <EventCategoryColor
          backgroundColor={
            maLegendValues[assetEventsKind]?.find(
              ({ category }) => category === selectEventCategory,
            )?.color
          }
          size={'10px'}
        />
        <EventCategoryName>
          {categoryOptions.find(({ value }) => value === selectEventCategory)?.label ??
            t('event_details_dialog.eventcategory_placeholder', 'Event Category')}
        </EventCategoryName>
      </EventCategory>
      <StyledSelect>
        <Controller
          name={`addMALCategory${index}`}
          id={`addMALCategory${index}`}
          rules={{
            required: true,
          }}
          defaultValue={null}
          render={({ onChange }) => (
            <Select
              onChange={(val) => {
                onChange(val);
                handleEventNameChange(val.value);
              }}
              placeholder={t(
                'event_details_dialog.dropdown_placeholder',
                'Please select an option',
              )}
              name={`addMALCategory${index}`}
              options={eventOptions}
              value={eventOptions?.find(({ value }) => value === selectEventName)}
              error={errors[`addMALCategory${index}`]?.type}
            />
          )}
        />
      </StyledSelect>
      <DialogDisplay>
        <div>
          <DialogLabel>
            {t('event_details_dialog.event_duration_date', 'Event Duration Date')}
          </DialogLabel>
          <DialogValue>
            <Controller
              control={control}
              name={`addEventDate${index}`}
              defaultValue={eventDate}
              render={({ onChange }) => (
                <DatePicker
                  selectsRange
                  className={'custom_datepicketstyle'}
                  startDate={eventDate.startDate}
                  endDate={eventDate.endDate}
                  onChange={(date) => {
                    onChange({
                      startDate: date[0],
                      endDate: date[1],
                    });
                    setEventDate({
                      startDate: date[0],
                      endDate: date[1],
                    });
                    if (
                      dayjs(date[0]).isAfter(
                        dayjs(dayjs().startOf('date').subtract(1, 'day')),
                        'day',
                      )
                    ) {
                      setDateError(true);
                    } else setDateError(false);
                  }}
                  onCalendarClose={() => {
                    if (!dayjs(eventDate.endDate).isValid()) {
                      setEventDate((prev) => ({
                        ...prev,
                        endDate: prev.startDate,
                      }));
                      onChange({
                        startDate: eventDate.startDate,
                        endDate: eventDate.startDate,
                      });
                    }
                  }}
                  ref={datepickerRef}
                  popperPlacement="bottom-start"
                  customInput={
                    <DateValue
                      onClick={() => {
                        datepickerRef.current.setOpen(true);
                      }}
                    >
                      {getCustomDateString()} <CalendarIcon />
                    </DateValue>
                  }
                  shouldCloseOnSelect={false}
                  onApplyClick={() => {
                    datepickerRef.current.setOpen(false);
                  }}
                />
              )}
            />
          </DialogValue>
          {dateError && <DialogValue>{eventDateError}</DialogValue>}
        </div>
        <StyledContainer>
          <div>
            <DialogLabel>{t('event_details_dialog.event_start_time', 'Start Time')}</DialogLabel>
            <StyledContainer>
              <Controller
                control={control}
                name={`addStartTime${index}`}
                defaultValue={startTime}
                render={({ onChange }) => (
                  <DatePicker
                    showTimeSelect
                    dateFormat={DateTimeFormats.DEFAULT_TIME_W_SECS}
                    timeFormat="HH:mm:ss"
                    onChange={(val) => {
                      onChange(val);
                      onStartTimeChange(val);
                    }}
                    customInput={
                      <div className="date-picker-member">
                        <Input
                          type="text"
                          placeholder="hh:mm:ss"
                          maxLength={8}
                          steps={2}
                          value={showStartTime}
                          onChange={(e) => setShowStartTime(formatTime(e))}
                        />
                      </div>
                    }
                    selected={startTime}
                    timeIntervals={1}
                    open={false}
                  />
                )}
              />
              <IconButton>
                <TimeIcon />
              </IconButton>
            </StyledContainer>
          </div>
          <div>
            <DialogLabel>{t('event_details_dialog.event_end_time', 'End Time')}</DialogLabel>
            <StyledContainer>
              <Controller
                control={control}
                name={`addEndTime${index}`}
                defaultValue={endTime}
                rules={{
                  required: true,
                  validate:
                    showEndTime.toString().replace(/:/g, '').length > 1
                      ? true
                      : 'Time must be in HH:MM:SS format',
                }}
                render={({ onChange }) => (
                  <DatePicker
                    showTimeSelect
                    dateFormat={DateTimeFormats.DEFAULT_TIME_W_SECS}
                    timeFormat="HH:mm:ss"
                    onChange={(val) => {
                      onChange(val);
                      onEndTimeChange(val);
                    }}
                    customInput={
                      <div className="date-picker-member">
                        <Input
                          type="text"
                          placeholder="hh:mm:ss"
                          maxLength={8}
                          steps={2}
                          value={showEndTime}
                          onChange={(e) => setShowEndTime(formatTime(e))}
                          error={showEndTime.toString().replaceAll(':', '').length < 1}
                        />
                      </div>
                    }
                    selected={endTime}
                    timeIntervals={1}
                    open={false}
                  />
                )}
              />
              <IconButton>
                <TimeIcon />
              </IconButton>
            </StyledContainer>
          </div>
        </StyledContainer>
        <div>
          <DialogLabel>{t('event_details_dialog.event_duration', 'Duration')}</DialogLabel>
          <DialogValue>{timeDuration}</DialogValue>
        </div>
      </DialogDisplay>
      <AddNoteFormRow>
        <EventNoteContainerHeading>
          <StyledIconButtonIcon className="notesIcon" icon={Icons.NOTE} size={12} />
          {t(`legend.${event?.a11yKey}.notes`, 'NOTES')}
        </EventNoteContainerHeading>
        <Controller
          control={control}
          name={`addMALNote${index}`}
          id={`addMALNote${index}`}
          rules={{
            required: true,
          }}
          defaultValue={noteEntered}
          render={({ onChange }) => (
            <Textarea
              placeholder={t('notes.description_placeholder', 'Add a note')}
              name={`addMALNote${index}`}
              value={noteEntered}
              onChange={(event) => {
                const val = event.target.value;
                onChange(val);
                onAddNoteChange(val);
              }}
            />
          )}
        />
      </AddNoteFormRow>
    </>
  );
};

AddEventDetailDialog.propTypes = {
  event: PropTypes.instanceOf(Object).isRequired,
  malCategoryOption: PropTypes.instanceOf(Object).isRequired,
  categoryOptions: PropTypes.instanceOf(Object).isRequired,
  eventOptions: PropTypes.instanceOf(Object).isRequired,
  calculateDuration: PropTypes.func.isRequired,
  index: PropTypes.number,
  eventDateDuration: PropTypes.Object,
  timezone: PropTypes.string,
  formatTime: PropTypes.func,
  assetEventsKind: PropTypes.string,
};
export default React.forwardRef(AddEventDetailDialog);
